import { For, Show, createMemo, createSignal, type Accessor, type JSX } from 'solid-js';
import type { GpuSnapshot, LlamacppSnapshot, LlamacppServerView, RouterModelView, TpsPoint } from '../state/metrics';
import { ctxFillPct, formatTokenCount } from './llamacpp-derive';
import { llamacppVramMb, placement } from './llamacpp-vram-derive';
import { InfoTip } from '../ui/info-tip';
import { HudTabs } from '../ui/hud-tabs';
import { Sparkline } from '../ui/sparkline';
import { selectedServerKey } from '../state/selection';
import { activeServerOf, emptyStateCopy, serverKey, serverLabel } from './llamacpp-card';

const TIPS = {
  tps:
    "How fast the model is writing, in tokens per second (roughly word-pieces per second). While it's generating this is a live rate (LIVE); when idle it holds the speed of the last answer (LAST).",
  quant:
    "How much the model's weights are compressed. Q4_K_M is a 4-bit medium-quality quantization — smaller and faster than the original 16-bit weights, at a small quality cost.",
  placement:
    "llama.cpp doesn't report how many layers sit on the GPU, so placement is inferred by comparing VRAM use to the model's file size: if less than the file is in VRAM, part of it runs on the slower CPU.",
  context:
    "How much of the model's memory window the conversation uses. When it fills up, the oldest messages start dropping off.",
  readWrite:
    "Two phases of a reply. Reading (prefill) is how fast the model digests your prompt — an average since the server started. Writing (decode) is live while generating.",
  avg:
    'Average reading and writing speed since the server started. Steadier than the live rate — useful for comparing models or quantizations.',
  queue:
    'Requests the server is handling right now, how many slots it has, and how many wait behind them. For a single user this is usually 1 of 1 with none waiting.',
  processed:
    'Total tokens the server has read and written since it started — the amount of work done, not a speed. Resets when you restart llama-server.',
  selected:
    'This machine is running more than one llama.cpp server. The numbers here describe the server picked in the runner card below — this chip names it.',
} as const;

function num(n: number): string {
  return n.toLocaleString('en-US');
}

function Stat(props: { label: string; tip?: string; children: JSX.Element }) {
  return (
    <div>
      <div class="hud-mono text-[0.5938rem] uppercase tracking-[0.22em] text-faint">
        {props.label}
        <Show when={props.tip}>{(t) => <> <InfoTip text={t()} /></>}</Show>
      </div>
      <div class="mt-1 text-[0.9375rem] font-semibold tabular-nums">{props.children}</div>
    </div>
  );
}

/**
 * Every fact the hero renders, read from ONE place: the selected server when a
 * choice exists, else the flat fields (which mirror the first/largest server and
 * are all an agent predating discovery sends). Before this the hero always showed
 * that one server and wore a chip admitting as much; now it follows the tab, which
 * is what the tab strip had promised all along.
 */
export type HeroView = {
  detected: boolean;
  model: string | null;
  version: string | null;
  quant: string | null;
  isSleeping: boolean | null;
  modelSizeMb: number | null;
  /** PID to attribute VRAM to: the router's child in router mode, else the listener. */
  vramPid: number | null;
  router: boolean | null;
  modelsLoaded: number | null;
  modelsMax: number | null;
  models: RouterModelView[] | null;
  modelsOmitted: number | null;
  tokensPerSec: number | null;
  tokensPerSecLive: number | null;
  promptTokensPerSec: number | null;
  requestsProcessing: number | null;
  requestsDeferred: number | null;
  ctxUsed: number | null;
  ctxLimit: number | null;
  ctxSource: string | null;
  avgTokensPerSec: number | null;
  avgPromptTokensPerSec: number | null;
  promptTokensTotal: number | null;
  tokensPredictedTotal: number | null;
  totalSlots: number | null;
  discoverySupported: boolean | null;
  /** The chip text naming the described server; null when there is no choice. */
  serverChip: string | null;
  /** Key for the per-server sparkline buffer; null → use the flat buffer. */
  tpsKey: string | null;
  /**
   * `true` when this view describes one server out of several. The name-match
   * VRAM fallback (see `vramMb` below) sums every `llama-server` process on the
   * box — safe only for the flat, single-server case (`false` here) where there
   * is exactly one to match; with a choice in play it would credit a remote or
   * unresolved server's VRAM to whichever local process happens to share its name.
   */
  perServer: boolean;
};

export function heroView(snap: LlamacppSnapshot | null, active: LlamacppServerView | null): HeroView {
  if (active && snap?.servers) {
    return {
      detected: active.detected,
      model: active.model, version: active.version, quant: active.quant, isSleeping: active.isSleeping,
      modelSizeMb: active.modelSizeMb, vramPid: active.serverPid ?? active.pid,
      router: active.router, modelsLoaded: active.modelsLoaded, modelsMax: active.modelsMax, models: active.models,
      modelsOmitted: active.modelsOmitted,
      tokensPerSec: active.tokensPerSec, tokensPerSecLive: active.tokensPerSecLive,
      promptTokensPerSec: active.promptTokensPerSec, requestsProcessing: active.requestsProcessing,
      requestsDeferred: active.requestsDeferred, ctxUsed: active.ctxUsed, ctxLimit: active.ctxLimit,
      ctxSource: active.ctxSource, avgTokensPerSec: active.avgTokensPerSec,
      avgPromptTokensPerSec: active.avgPromptTokensPerSec, promptTokensTotal: active.promptTokensTotal,
      tokensPredictedTotal: active.tokensPredictedTotal, totalSlots: active.totalSlots,
      discoverySupported: snap.discoverySupported,
      serverChip: serverLabel(active, snap.servers),
      tpsKey: serverKey(active),
      perServer: true,
    };
  }
  return {
    detected: snap?.detected ?? false,
    model: snap?.model ?? null, version: snap?.version ?? null, quant: snap?.quant ?? null,
    isSleeping: snap?.isSleeping ?? null, modelSizeMb: snap?.modelSizeMb ?? null, vramPid: snap?.serverPid ?? null,
    router: snap?.router ?? null, modelsLoaded: snap?.modelsLoaded ?? null, modelsMax: snap?.modelsMax ?? null,
    models: snap?.models ?? null,
    modelsOmitted: snap?.modelsOmitted ?? null,
    tokensPerSec: snap?.tokensPerSec ?? null, tokensPerSecLive: snap?.tokensPerSecLive ?? null,
    promptTokensPerSec: snap?.promptTokensPerSec ?? null, requestsProcessing: snap?.requestsProcessing ?? null,
    requestsDeferred: snap?.requestsDeferred ?? null, ctxUsed: snap?.ctxUsed ?? null, ctxLimit: snap?.ctxLimit ?? null,
    ctxSource: snap?.ctxSource ?? null, avgTokensPerSec: snap?.avgTokensPerSec ?? null,
    avgPromptTokensPerSec: snap?.avgPromptTokensPerSec ?? null, promptTokensTotal: snap?.promptTokensTotal ?? null,
    tokensPredictedTotal: snap?.tokensPredictedTotal ?? null, totalSlots: snap?.totalSlots ?? null,
    discoverySupported: snap?.discoverySupported ?? null,
    serverChip: null,
    tpsKey: null,
    perServer: false,
  };
}

/**
 * True when no VRAM may be attributed to the described server: it has no
 * resolvable PID and a name-match would credit other `llama-server` processes
 * to it — a selected remote/configured target, or a router whose child was not
 * resolved. The flat, single-server case keeps the name-match fallback (there is
 * exactly one server, so the match is safe). Shared by the hero's VRAM row and
 * the cockpit's GPU cluster so the two halves cannot disagree.
 */
export function vramUnattributable(v: HeroView): boolean {
  return v.vramPid == null && (v.perServer || v.router === true);
}

export function ModelHero(props: {
  data: Accessor<LlamacppSnapshot | null>;
  tps: Accessor<TpsPoint[]>;
  tpsByServer: Accessor<Record<string, TpsPoint[]>>;
  gpu?: Accessor<GpuSnapshot | null>;
}) {
  const snap = () => props.data();
  const hv = createMemo(() => heroView(snap(), activeServerOf(snap()?.servers, selectedServerKey())));
  const tpsPoints = () => {
    const k = hv().tpsKey;
    return k ? (props.tpsByServer()[k] ?? []) : props.tps();
  };
  // Nullable throughout: `null` means "the catalog didn't answer" (unknown), a
  // possibly-empty array means the catalog answered and said so. Coalescing this
  // to `[]` at the top would make an unanswered catalog indistinguishable from an
  // answered-and-empty one to every consumer below — each read site below decides
  // which of the two it means.
  const models = (): RouterModelView[] | null => hv().models;
  const activeId = () => models()?.find((m) => m.active)?.id ?? null;

  const [pickedId, setPickedId] = createSignal<string | null>(null);
  const selectedId = createMemo(() => {
    const picked = pickedId();
    if (picked && models()?.some((m) => m.id === picked)) return picked;
    return activeId();
  });
  const viewingActive = () => selectedId() == null || selectedId() === activeId();
  const selected = () => models()?.find((m) => m.id === selectedId()) ?? null;

  /**
   * No llama.cpp server has ever been detected on this machine — either `snap()` is
   * `null` because the agent has nothing to report yet, or the agent positively said
   * `detected: false` (it sends that every tick once it has polled at all — absence
   * is data, not a missing frame). Must be checked first: every other mode below
   * reads fields off `snap()` and would otherwise mistake "nothing to ask" or "asked,
   * nothing there" for "asked, empty".
   */
  const none = () => snap() == null || !hv().detected;
  /**
   * Standby: a confirmed router (`router === true`) positively reported nothing
   * loaded. Requiring `router === true` — not just `modelsLoaded === 0` — matters
   * because the copy below says "router · up"; without that positive evidence this
   * would describe a router that was never confirmed to exist.
   */
  const standby = () => hv().router === true && hv().modelsLoaded === 0;
  /**
   * A confirmed router's catalog poll didn't answer this round: `model` and
   * `modelsLoaded` are both unknown. Requires `router === true` for the same reason
   * as `standby` — this mode's copy names a "router" and a "catalog", neither of
   * which is true to say about a plain single-model server that simply hasn't
   * answered `/props` yet (that falls through to the honest em-dash in `live`
   * instead). Must not fall into `standby` — a `?? 0` there would turn "we don't
   * know" into "confirmed empty" and the hero would claim no model is loaded while
   * one may well be generating right now.
   */
  const catalogUnknown = () =>
    hv().router === true && hv().modelsLoaded == null && hv().model == null;
  const mode = (): 'none' | 'standby' | 'unknown' | 'live' => {
    if (none()) return 'none';
    if (standby()) return 'standby';
    if (catalogUnknown()) return 'unknown';
    return 'live';
  };

  const generating = () => (hv().requestsProcessing ?? 0) >= 1;
  const speed = () => (generating() ? (hv().tokensPerSecLive ?? hv().tokensPerSec) : hv().tokensPerSec);

  // A null `vramPid` means the process holding the model was never resolved (a
  // router child that died between /v1/models and the /proc scan, an unreadable
  // /proc, a remote/configured target) — NOT "no PID known, so match by name".
  // The name-match fallback inside `llamacppVramMb` sums every process called
  // `llama-server` on the box: with a router, or with a server picked out of
  // several (`perServer`), that would credit an unrelated local process (or
  // another server's) VRAM to this one. It stays available only for the flat,
  // single-server case it was written for — `vramUnattributable` is the single
  // definition of that rule, shared with the cockpit's GPU cluster.
  const vramMb = () => {
    const v = hv();
    if (vramUnattributable(v)) return null;
    return llamacppVramMb(props.gpu?.() ?? null, v.vramPid);
  };
  const place = () => placement(vramMb(), hv().modelSizeMb, hv().isSleeping);

  const ctxIsPeak = () => hv().ctxSource === 'peak';
  const fillPct = () => ctxFillPct(hv().ctxUsed, hv().ctxLimit);

  // A real, known directory with more than one entry. An unknown catalog
  // (`models() == null`) never has tabs — there is nothing confirmed to switch between.
  // Also hidden in standby: with nothing loaded there is no active tab to select, so
  // roving tabindex would leave every tab keyboard-unreachable while the same list is
  // already shown by the Standby directory below.
  const hasTabs = () => mode() !== 'standby' && (models()?.length ?? 0) > 1;
  const tabs = () =>
    (models() ?? []).map((m) => ({
      id: m.id,
      label: m.id,
      live: m.active && generating(),
      detail: m.loaded ? undefined : '· idle',
    }));

  return (
    <div>
      <Show when={hasTabs()}>
        <HudTabs
          items={tabs()}
          selected={selectedId() ?? ''}
          onSelect={setPickedId}
          label="Models in the router directory"
        />
      </Show>

      <Show when={mode() === 'none'}>
        <NoServer discoverySupported={hv().discoverySupported} />
      </Show>
      <Show when={mode() === 'standby'}>
        <Standby models={models() ?? []} omitted={hv().modelsOmitted} />
      </Show>
      <Show when={mode() === 'unknown'}>
        <CatalogUnknown />
      </Show>
      <Show when={mode() === 'live'}>
        <Show when={viewingActive()} fallback={<Passport model={selected()} />}>
          <div class="flex flex-wrap items-center gap-2.5">
            <span class="text-[1.375rem] font-semibold tracking-tight">{hv().model ?? '—'}</span>
            {/* This hero describes the server picked in the llama.cpp card's own tab
                strip below it — this chip names it. Shown only once a choice actually
                exists (`hv().serverChip` is null with fewer than two servers, the same
                threshold that puts up the tab strip and gates the card's per-server
                rows), so a lone server never grows a label nobody asked for. */}
            <Show when={hv().serverChip}>
              {(chip) => (
                <>
                  <span class="hud-clip hud-mono border border-line bg-accent-soft px-2.5 py-0.5 text-[0.6563rem] text-muted">
                    {chip()}
                  </span>
                  <InfoTip text={TIPS.selected} />
                </>
              )}
            </Show>
            <Show when={hv().quant}>
              {(q) => (
                <>
                  <span class="hud-clip hud-mono border border-line bg-accent-soft px-2.5 py-0.5 text-[0.6563rem] text-muted">
                    {q()}
                  </span>
                  <InfoTip text={TIPS.quant} />
                </>
              )}
            </Show>
            <Show when={place() === 'gpu' || place() === 'partial'}>
              <span
                class={`hud-clip hud-mono border px-2.5 py-0.5 text-[0.6563rem] ${
                  place() === 'gpu'
                    ? 'border-good/40 bg-good-soft text-good'
                    : 'border-warn/40 bg-warn-soft text-warn'
                }`}
              >
                {place() === 'gpu' ? '✓ all on GPU' : '◐ partly on CPU'}
              </span>
              <InfoTip text={TIPS.placement} />
            </Show>
          </div>

          <div class="hud-mono mt-1.5 text-[0.6875rem] tracking-[0.08em] text-faint">
            llama.cpp {hv().version ?? '—'}
            <Show when={hv().router}> · router</Show>
          </div>

          <div class="mt-4 flex flex-wrap items-end gap-5">
            <div>
              <div class="text-[4rem] font-light leading-[0.92] tabular-nums text-holo max-[500px]:text-[3.25rem]">
                {speed() != null ? speed()!.toFixed(1) : '—'}
              </div>
              <div class="hud-mono text-[0.6875rem] uppercase tracking-[0.12em] text-muted">
                tok/s <InfoTip text={TIPS.tps} />{' '}
                <Show when={speed() != null}>
                  <span class={generating() ? 'text-good' : 'text-faint'}>
                    {generating() ? '● LIVE' : 'LAST'}
                  </span>
                </Show>
              </div>
            </div>
            <Show when={tpsPoints().length >= 2}>
              <div class="min-w-[200px] flex-1">
                <Sparkline points={tpsPoints().map((p) => p.tps)} color="var(--holo)" class="h-[58px]" />
              </div>
            </Show>
          </div>

          <div class="mt-4 flex flex-wrap gap-x-6 gap-y-3 border-t border-line pt-3.5">
            <Stat label="Context" tip={TIPS.context}>
              <Show when={hv().ctxUsed != null} fallback="—">
                {ctxIsPeak() ? 'up to ' : ''}
                {num(hv().ctxUsed!)}
                <Show when={hv().ctxLimit != null}>
                  <small class="font-normal text-muted">
                    {' '}/ {num(hv().ctxLimit!)}
                    <Show when={fillPct() != null}> ({fillPct()}%)</Show>
                    <Show when={ctxIsPeak()}> · peak</Show>
                  </small>
                </Show>
              </Show>
            </Stat>

            <Stat label="Read / write" tip={TIPS.readWrite}>
              {hv().promptTokensPerSec != null ? hv().promptTokensPerSec!.toFixed(1) : '—'}
              <small class="font-normal text-muted"> avg</small> /{' '}
              {speed() != null ? speed()!.toFixed(1) : '—'}
              <small class="font-normal text-muted"> tok/s</small>
            </Stat>

            <Show when={hv().avgTokensPerSec != null || hv().avgPromptTokensPerSec != null}>
              <Stat label="Avg · session" tip={TIPS.avg}>
                {hv().avgPromptTokensPerSec != null ? hv().avgPromptTokensPerSec!.toFixed(1) : '—'} /{' '}
                {hv().avgTokensPerSec != null ? hv().avgTokensPerSec!.toFixed(1) : '—'}
                <small class="font-normal text-muted"> tok/s</small>
              </Stat>
            </Show>

            <Stat label="Queue" tip={TIPS.queue}>
              {hv().requestsProcessing ?? '—'}
              <small class="font-normal text-muted">
                <Show when={hv().totalSlots != null}> / {hv().totalSlots}</Show> ·{' '}
                {hv().requestsDeferred ?? '—'} waiting
              </small>
            </Stat>

            <Show when={hv().promptTokensTotal != null || hv().tokensPredictedTotal != null}>
              <Stat label="Processed" tip={TIPS.processed}>
                {hv().promptTokensTotal != null ? formatTokenCount(hv().promptTokensTotal!) : '—'} /{' '}
                {hv().tokensPredictedTotal != null ? formatTokenCount(hv().tokensPredictedTotal!) : '—'}
                <small class="font-normal text-muted"> tok</small>
              </Stat>
            </Show>
          </div>
        </Show>
      </Show>
    </div>
  );
}

/**
 * No llama.cpp server was ever detected on this machine — this half of the cockpit
 * has nothing to report. Distinct from `Standby` (a detected router confirming no
 * model loaded, which implies a server that could start one on request): here there
 * is no server at all, so no model name, quant, or "standby" wording is shown.
 *
 * The paragraph comes from `emptyStateCopy`: a platform without `/proc`
 * (`discoverySupported === false`) never ran auto-detect, so it gets the "not
 * available on this platform" wording instead of a claim that a search came up
 * empty — and the "not detected" chip is suppressed there for the same reason.
 */
function NoServer(props: { discoverySupported: boolean | null }) {
  return (
    <div>
      <div class="flex flex-wrap items-center gap-2.5">
        <span class="text-[1.375rem] font-semibold tracking-tight text-muted">llama.cpp</span>
        {/* The chip is the loud half of this state, so on a platform that cannot
            look it must not say "not detected" — that is a search result, and no
            search ran. It is dropped rather than reworded: the paragraph below
            already states the one fact there is. */}
        <Show when={props.discoverySupported !== false}>
          <span class="hud-clip hud-mono border border-line bg-accent-soft px-2.5 py-0.5 text-[0.6563rem] text-muted">
            not detected
          </span>
        </Show>
      </div>
      <p class="hud-mono mt-1.5 text-[0.6875rem] tracking-[0.08em] text-faint">
        {emptyStateCopy(props.discoverySupported)}
      </p>
    </div>
  );
}

/** Router up, catalog positively confirmed nothing loaded — show the directory instead of an empty hero. */
function Standby(props: { models: RouterModelView[]; omitted: number | null }) {
  return (
    <div>
      <div class="flex flex-wrap items-center gap-2.5">
        <span class="text-[1.375rem] font-semibold tracking-tight text-muted">Standby</span>
        <span class="hud-clip hud-mono border border-line bg-accent-soft px-2.5 py-0.5 text-[0.6563rem] text-muted">
          router · up
        </span>
      </div>
      <p class="hud-mono mt-1.5 text-[0.6875rem] tracking-[0.08em] text-faint">
        no model loaded — the router starts one on the first request
      </p>
      <Show when={props.models.length > 0 || (props.omitted ?? 0) > 0}>
        <ul class="mt-3.5 max-w-[480px] border-t border-line">
          <For each={props.models}>
            {(m) => (
              <li class="flex items-center justify-between gap-3 border-b border-line/50 py-2 text-xs last:border-b-0">
                <span class="font-semibold">{m.id}</span>
                <span class="tabular-nums text-faint">
                  {m.sizeMb != null ? `${(m.sizeMb / 1024).toFixed(1)} GB · ` : ''}
                  {m.loaded ? 'loaded' : 'idle'}
                </span>
              </li>
            )}
          </For>
          <Show when={(props.omitted ?? 0) > 0}>
            <li data-testid="catalogue-more" class="py-2 text-xs text-muted">
              +{props.omitted} more
            </li>
          </Show>
        </ul>
      </Show>
    </div>
  );
}

/**
 * Router up, but its catalog poll (`/v1/models`) didn't answer this round — whether
 * anything is loaded, and what, is genuinely unknown right now. Distinct from
 * `Standby`: that state is a confirmed fact from the router, this is the absence
 * of one. Never says "no model loaded" — that would assert a negative we don't have.
 */
function CatalogUnknown() {
  return (
    <div>
      <div class="flex flex-wrap items-center gap-2.5">
        <span class="text-[1.375rem] font-semibold tracking-tight text-muted">Router</span>
        <span class="hud-clip hud-mono border border-line bg-accent-soft px-2.5 py-0.5 text-[0.6563rem] text-muted">
          catalog unknown
        </span>
      </div>
      <p class="hud-mono mt-1.5 text-[0.6875rem] tracking-[0.08em] text-faint">
        the model catalog didn't answer this poll — what it's serving right now is unknown
      </p>
    </div>
  );
}

/** A model the router knows but isn't currently serving: facts only, no metrics. */
function Passport(props: { model: RouterModelView | null }) {
  return (
    <Show when={props.model}>
      {(m) => (
        <div>
          <div class="flex flex-wrap items-center gap-2.5">
            <span class="text-[1.375rem] font-semibold tracking-tight">{m().id}</span>
            <span
              class={`hud-clip hud-mono border px-2.5 py-0.5 text-[0.6563rem] ${
                m().loaded ? 'border-good/40 bg-good-soft text-good' : 'border-line bg-accent-soft text-muted'
              }`}
            >
              {m().loaded ? 'loaded' : 'idle'}
            </span>
          </div>
          <dl class="mt-4 flex flex-wrap gap-x-6 gap-y-3">
            <div>
              <dt class="hud-mono text-[0.5938rem] uppercase tracking-[0.22em] text-faint">Size</dt>
              <dd class="mt-1 text-[0.9375rem] font-semibold tabular-nums">
                {m().sizeMb != null ? `${(m().sizeMb! / 1024).toFixed(1)} GB` : '—'}
              </dd>
            </div>
          </dl>
          <p class="mt-4 border-t border-line pt-3.5 text-[0.8rem] text-muted">
            Speed, context and queue are not shown here: live metrics follow the active model — llama.cpp
            reports them only for the model it is serving right now.
          </p>
        </div>
      )}
    </Show>
  );
}
